// src/services/purchaseOrderService.js - COMPLETE UPDATED VERSION
import ApiService from './api'
import AppURLs from '../config/urls'
import GRNService from './grnService'

class PurchaseOrderService {
  /**
   * Get all purchase orders with filtering
   * @param {Object} params - Query parameters for filtering
   * @returns {Promise<Object>} - API response
   */
  async getAllPurchaseOrders(params = {}) {
    try {
      console.log('📋 Fetching purchase orders with params:', params)
      
      const response = await ApiService.get(AppURLs.PURCHASE_ORDERS.GET_ALL, params)
      
      console.log('✅ Purchase orders fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching purchase orders:', error)
      throw error
    }
  }

  /**
   * Get purchase order by ID
   * @param {string} id - Purchase order ID
   * @returns {Promise<Object>} - API response
   */
  async getPurchaseOrderById(id) {
    try {
      console.log('📋 Fetching purchase order by ID:', id)
      
      if (!id) {
        throw new Error('Purchase order ID is required')
      }

      const endpoint = ApiService.replaceUrlParams(AppURLs.PURCHASE_ORDERS.GET_BY_ID, { id })
      const response = await ApiService.get(endpoint)
      
      console.log('✅ Purchase order fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching purchase order:', error)
      throw error
    }
  }

  /**
   * Create new purchase order
   * @param {Object} orderData - Purchase order data
   * @returns {Promise<Object>} - API response
   */
  async createPurchaseOrder(orderData) {
    try {
      console.log('📋 Creating purchase order with data:', orderData)
      
      const response = await ApiService.post(AppURLs.PURCHASE_ORDERS.CREATE, orderData)
      
      console.log('✅ Purchase order created successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error creating purchase order:', error)
      throw error
    }
  }

  /**
   * Update existing purchase order
   * @param {string} id - Purchase order ID
   * @param {Object} orderData - Updated purchase order data
   * @returns {Promise<Object>} - API response
   */
  async updatePurchaseOrder(id, orderData) {
    try {
      console.log('📋 Updating purchase order:', id, orderData)
      
      if (!id) {
        throw new Error('Purchase order ID is required')
      }

      const endpoint = ApiService.replaceUrlParams(AppURLs.PURCHASE_ORDERS.UPDATE, { id })
      const response = await ApiService.patch(endpoint, orderData)
      
      console.log('✅ Purchase order updated successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error updating purchase order:', error)
      throw error
    }
  }

  /**
   * Update Purchase Order status with AUTO GRN CREATION - NEW ENHANCED METHOD
   * @param {string} poId - Purchase Order ID
   * @param {string} newStatus - New status for the PO
   * @param {Object} poData - Complete PO data (optional, will fetch if not provided)
   * @param {string} receivedBy - Who received the goods (for auto GRN)
   * @returns {Promise<Object>} - Result of status update and potential GRN creation
   */
  async updatePurchaseOrderStatus(poId, newStatus, poData = null, receivedBy = 'System Auto-Creation') {
    try {
      console.log('📋 Updating PO status with auto GRN logic:', { poId, newStatus })
      
      if (!poId) {
        throw new Error('Purchase order ID is required')
      }

      if (!newStatus) {
        throw new Error('New status is required')
      }

      // Step 1: Update the PO status
      const endpoint = AppURLs?.PURCHASE_ORDERS?.UPDATE_STATUS 
        ? ApiService.replaceUrlParams(AppURLs.PURCHASE_ORDERS.UPDATE_STATUS, { id: poId })
        : `/purchase-orders/${poId}/status`
      
      const statusUpdateResponse = await ApiService.patch(endpoint, { 
        status: newStatus,
        updatedAt: new Date().toISOString()
      })
      
      if (!statusUpdateResponse?.success) {
        throw new Error('Failed to update PO status')
      }

      console.log('✅ PO status updated successfully:', statusUpdateResponse)

      // Step 2: If status is 'completed', automatically create GRN
      if (newStatus.toLowerCase() === 'completed') {
        console.log('📦 PO marked as completed, initiating auto GRN creation...')
        
        try {
          // Get complete PO data if not provided
          let completePO = poData
          if (!completePO) {
            console.log('📋 Fetching complete PO data for GRN creation...')
            const poResponse = await this.getPurchaseOrderById(poId)
            if (poResponse?.success && poResponse.data) {
              completePO = poResponse.data
            } else {
              throw new Error('Failed to fetch complete PO data')
            }
          }

          // Create GRN automatically
          const grnResult = await GRNService.autoCreateGRNFromCompletedPO(completePO, receivedBy)
          
          if (grnResult?.success) {
            console.log('✅ GRN created automatically for completed PO:', grnResult.data)
            
            return {
              success: true,
              message: `PO marked as completed and GRN ${grnResult.data.grnNumber} created automatically`,
              data: {
                purchaseOrder: statusUpdateResponse.data,
                grn: grnResult.data,
                autoGRNCreated: true
              }
            }
          } else {
            console.error('❌ Failed to create GRN for completed PO:', grnResult)
            
            return {
              success: true, // PO status was updated successfully
              message: 'PO marked as completed but GRN creation failed',
              warning: 'Auto GRN creation failed',
              data: {
                purchaseOrder: statusUpdateResponse.data,
                grnError: grnResult?.message || 'Unknown error creating GRN',
                autoGRNCreated: false
              }
            }
          }
          
        } catch (grnError) {
          console.error('❌ Exception during auto GRN creation:', grnError)
          
          return {
            success: true, // PO status was updated successfully
            message: 'PO marked as completed but GRN creation failed',
            warning: 'Auto GRN creation encountered an error',
            data: {
              purchaseOrder: statusUpdateResponse.data,
              grnError: grnError.message || 'Exception during GRN creation',
              autoGRNCreated: false
            }
          }
        }
      }
      
      // For other status changes, just return the update result
      return {
        success: true,
        message: `PO status updated to ${newStatus}`,
        data: {
          purchaseOrder: statusUpdateResponse.data,
          autoGRNCreated: false
        }
      }
      
    } catch (error) {
      console.error('❌ Error updating PO status:', error)
      throw error
    }
  }

  /**
   * Batch update multiple PO statuses
   * @param {Array} updates - Array of {id, status} objects
   * @returns {Promise<Object>} - Batch update results
   */
  async batchUpdatePOStatus(updates) {
    try {
      console.log('📋 Batch updating PO statuses:', updates)
      
      const results = []
      const errors = []
      
      for (const update of updates) {
        try {
          const result = await this.updatePurchaseOrderStatus(update.id, update.status)
          results.push({
            id: update.id,
            status: update.status,
            success: true,
            result: result
          })
        } catch (error) {
          console.error(`❌ Failed to update PO ${update.id}:`, error)
          errors.push({
            id: update.id,
            status: update.status,
            success: false,
            error: error.message
          })
        }
      }
      
      return {
        success: errors.length === 0,
        message: `${results.length} POs updated successfully, ${errors.length} failed`,
        data: {
          successful: results,
          failed: errors,
          summary: {
            total: updates.length,
            successful: results.length,
            failed: errors.length,
            autoGRNsCreated: results.filter(r => r.result.data?.autoGRNCreated).length
          }
        }
      }
      
    } catch (error) {
      console.error('❌ Error in batch update:', error)
      throw error
    }
  }

  /**
   * Delete purchase order
   * @param {string} id - Purchase order ID
   * @returns {Promise<Object>} - API response
   */
  async deletePurchaseOrder(id) {
    try {
      console.log('📋 Deleting purchase order:', id)
      
      if (!id) {
        throw new Error('Purchase order ID is required')
      }

      const endpoint = ApiService.replaceUrlParams(AppURLs.PURCHASE_ORDERS.DELETE, { id })
      const response = await ApiService.delete(endpoint)
      
      console.log('✅ Purchase order deleted successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error deleting purchase order:', error)
      throw error
    }
  }

  /**
   * Get suppliers and warehouses for purchase orders
   * @returns {Promise<Object>} - API response with suppliers and warehouses
   */
  async getSuppliersAndWarehouses() {
    try {
      console.log('🏪 Fetching suppliers and warehouses for purchase orders')
      
      // Fetch suppliers and branches in parallel
      const [suppliersResponse, branchesResponse] = await Promise.all([
        ApiService.get('/suppliers').catch(err => {
          console.warn('Suppliers endpoint failed:', err)
          return { success: false, data: [] }
        }),
        ApiService.get('/branches').catch(err => {
          console.warn('Branches endpoint failed:', err)
          return { success: false, data: [] }
        })
      ])
      
      const suppliers = suppliersResponse?.success ? (suppliersResponse.data?.suppliers || suppliersResponse.data || []) : []
      const branches = branchesResponse?.success ? (branchesResponse.data?.branches || branchesResponse.data || []) : []
      const warehouses = branches.filter(branch => branch.type === 'warehouse')
      
      return {
        success: true,
        data: {
          suppliers,
          warehouses,
          summary: {
            totalSuppliers: suppliers.length,
            totalWarehouses: warehouses.length,
            totalOptions: suppliers.length + warehouses.length
          }
        }
      }
    } catch (error) {
      console.error('❌ Error fetching suppliers and warehouses:', error)
      
      // Try individual fallback calls
      try {
        console.log('🔄 Trying fallback approach...')
        const [suppliersResponse, branchesResponse] = await Promise.all([
          ApiService.get('/suppliers'),
          ApiService.get('/branches')
        ])
        
        const suppliers = suppliersResponse?.success ? (suppliersResponse.data?.suppliers || []) : []
        const branches = branchesResponse?.success ? (branchesResponse.data?.branches || []) : []
        const warehouses = branches.filter(branch => branch.type === 'warehouse')
        
        return {
          success: true,
          data: {
            suppliers,
            warehouses,
            summary: {
              totalSuppliers: suppliers.length,
              totalWarehouses: warehouses.length,
              totalOptions: suppliers.length + warehouses.length
            }
          }
        }
      } catch (fallbackError) {
        console.error('❌ Fallback fetch also failed:', fallbackError)
        throw error
      }
    }
  }

  /**
   * Get all branches
   * @returns {Promise<Object>} - API response
   */
  async getAllBranches() {
    try {
      console.log('🏢 Fetching all branches')
      
      const response = await ApiService.get('/branches')
      
      console.log('✅ Branches fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching branches:', error)
      throw error
    }
  }

  /**
   * Get purchase order status options
   * @returns {Array} - Array of status options
   */
  getStatusOptions() {
    return [
      { value: '', label: 'All Statuses' },
      { value: 'pending', label: 'Pending' },
      { value: 'approved', label: 'Approved' },
      { value: 'completed', label: 'Completed' },
      { value: 'cancelled', label: 'Cancelled' }
    ]
  }

  /**
   * Get supplier type options
   * @returns {Array} - Array of supplier type options
   */
  getSupplierTypeOptions() {
    return [
      { value: '', label: 'All Types' },
      { value: 'supplier', label: 'Supplier' },
      { value: 'warehouse', label: 'Warehouse' }
    ]
  }

  /**
   * Format purchase order status for display
   * @param {string} status - Purchase order status
   * @returns {Object} - Formatted status object
   */
  formatStatus(status) {
    const statusMap = {
      'pending': { label: 'Pending', color: 'orange', bgColor: 'bg-orange-100', textColor: 'text-orange-800' },
      'approved': { label: 'Approved', color: 'blue', bgColor: 'bg-blue-100', textColor: 'text-blue-800' },
      'completed': { label: 'Completed', color: 'green', bgColor: 'bg-green-100', textColor: 'text-green-800' },
      'cancelled': { label: 'Cancelled', color: 'red', bgColor: 'bg-red-100', textColor: 'text-red-800' }
    }

    return statusMap[status] || { label: status, color: 'gray', bgColor: 'bg-gray-100', textColor: 'text-gray-800' }
  }

  /**
   * Format currency amount
   * @param {number} amount - Amount to format
   * @returns {string} - Formatted currency string
   */
  formatCurrency(amount) {
    if (!amount && amount !== 0) return 'Rs. 0.00'
    
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount).replace('LKR', 'Rs.')
  }

  /**
   * Check if PO can have status changed to completed
   * @param {Object} po - Purchase order object
   * @returns {Object} - Validation result
   */
  canMarkAsCompleted(po) {
    if (!po) {
      return { canComplete: false, reason: 'Purchase order data is missing' }
    }

    if (po.status === 'completed') {
      return { canComplete: false, reason: 'Purchase order is already completed' }
    }

    if (po.status === 'cancelled') {
      return { canComplete: false, reason: 'Cannot complete a cancelled purchase order' }
    }

    if (!po.orderItems || po.orderItems.length === 0) {
      return { canComplete: false, reason: 'Purchase order has no items' }
    }

    return { canComplete: true, reason: 'Purchase order can be marked as completed' }
  }

  /**
   * Get POs that are available for GRN creation (pending status only)
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} - API response with pending POs only
   */
  async getPOsForGRNCreation(params = {}) {
    try {
      console.log('📋 Fetching POs available for GRN creation (pending only)...')
      
      // Always filter for pending status
      const queryParams = { ...params, status: 'pending' }
      
      const response = await this.getAllPurchaseOrders(queryParams)
      
      if (response?.success) {
        const pos = response.data?.purchaseOrders || response.data || []
        
        // Additional client-side filtering to ensure only pending POs
        const pendingPOs = pos.filter(po => {
          const status = po.status?.toLowerCase() || 'pending'
          return status === 'pending'
        })
        
        console.log(`✅ Found ${pendingPOs.length} pending POs available for GRN creation`)
        
        return {
          ...response,
          data: {
            ...response.data,
            purchaseOrders: pendingPOs,
            message: `${pendingPOs.length} pending POs available for GRN creation`
          }
        }
      }
      
      return response
    } catch (error) {
      console.error('❌ Error fetching POs for GRN creation:', error)
      throw error
    }
  }
}

export default new PurchaseOrderService()